import { useCallback, useEffect, useRef, useState } from "react";

interface Props {
  onMinuteChange: () => void;
  minutesFromStorage: number;
}

// Used only to make minutes faster for testing in dev
const secondsInMinute = 60;

export default function useTimer({
  onMinuteChange,
  minutesFromStorage,
}: Props) {
  const [seconds, setSeconds] = useState(0);
  const interval = useRef<ReturnType<typeof setInterval>>();

  // counter the seconds that are in storage compared to the seconds that have been counted in this session
  // e.g. if the user has 1 minutes in storage and 1 minute has passed in this session, then all minutes came from this second and we want to start counting from 0
  // but if the user has 2 minutes in storage and 1 minute has passed in this session, then 1 minute came from storage and we want to start counting from 60
  const secondsCountedSoFar =
    Math.floor(seconds / secondsInMinute) * secondsInMinute;

  const onMinuteChangeCalls = useRef<Set<number>>(new Set());
  const onMinuteChangeIdempotent = useCallback(
    (minutes: number) => {
      console.log({ minutes }, "idempotent");
      if (onMinuteChangeCalls.current.has(minutes)) {
        return;
      }

      onMinuteChangeCalls.current.add(minutes);
      onMinuteChange();
    },
    [onMinuteChange]
  );

  const stop = () => {
    clearInterval(interval.current);
  };

  const start = useCallback(() => {
    if (interval.current) {
      clearInterval(interval.current);
    }

    interval.current = setInterval(() => {
      setSeconds((prev) => prev + 1);
    }, 1000);

    return () => {
      clearInterval(interval.current);
    };
  }, []);

  useEffect(() => {
    // Don't run on first render
    if (seconds < secondsInMinute) {
      return;
    }

    if (seconds % secondsInMinute === 0) {
      onMinuteChangeIdempotent(seconds / secondsInMinute);
    }
  }, [onMinuteChangeIdempotent, seconds]);

  return {
    seconds:
      seconds + minutesFromStorage * secondsInMinute - secondsCountedSoFar,
    start,
    stop,
  };
}
